/**
 * Liner-notes text helpers. `tracks.notes` arrives as (possibly) HTML from
 * the prose editor; jacket faces set the text themselves in the §3.3 voice,
 * so tags are stripped and paragraphs re-split — the same approach the
 * immersive feed takes today, extracted for the jacket shells.
 */

export function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, "")
}

/**
 * Paragraph blocks for the 40ch liner-notes column (and its type stagger).
 *
 * Only real paragraph boundaries split (`</p>`/`</div>`); a `<br>` is a soft
 * break and becomes a space. TipTap's Enter authors one `<p>` per sentence,
 * which is indistinguishable from real paragraphs by markup alone — so when
 * the median block runs short (< 90 chars), the "paragraphs" are sentences
 * and the whole note merges into one flowing block. Genuinely multi-paragraph
 * notes (long blocks) keep their breaks.
 */
const MERGE_MEDIAN_CHARS = 90

export function notesBlocks(notes: string): string[] {
  const withBreaks = notes
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<\/(?:p|div)>/gi, "\n")
  const blocks = stripHtml(withBreaks)
    .split(/\n+/)
    .map((block) => block.replace(/\s+/g, " ").trim())
    .filter(Boolean)
  if (blocks.length < 2) return blocks
  const lengths = blocks.map((block) => block.length).sort((a, b) => a - b)
  const median = lengths[Math.floor((lengths.length - 1) / 2)]
  return median < MERGE_MEDIAN_CHARS ? [blocks.join(" ")] : blocks
}

/** The first sentence of the (stripped) notes — Up Next preview copy.
 * Block boundaries and soft breaks become spaces first: bare stripHtml
 * would glue "…touch.</p><p>This…" into "touch.This" and the sentence
 * split (which needs whitespace after the terminator) would run on. */
export function firstSentence(notes: string): string {
  const text = stripHtml(notes.replace(/<br\s*\/?>|<\/(?:p|div)>/gi, " "))
    .replace(/\s+/g, " ")
    .trim()
  return text.split(/(?<=[.!?])\s+/)[0] ?? text
}

/**
 * The phrase the curator emphasized — the target of Candidate B's
 * highlighter stroke. Deterministic: the first <em>/<strong> phrase when
 * the curator marked one, else the last three words of the first sentence.
 * Capped at three words so the stroke stays on a single line.
 */
export function findEmphasisPhrase(notes: string): string | null {
  const marked = /<(em|strong|i|b)[^>]*>([\s\S]*?)<\/\1>/i.exec(notes)
  const candidate = marked
    ? stripHtml(marked[2])
    : firstSentence(notes).replace(/[.!?]+$/, "")
  const words = candidate.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return null
  return words.slice(-3).join(" ")
}
